import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { admin, tournaments as tournamentsApi, tournamentAdmin } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Search, Trophy, Trash2, Edit, MapPin, Calendar, Users,
  ChevronLeft, ChevronRight, Euro, UserPlus, Eye, Swords,
  Download, FileSpreadsheet, UserMinus
} from 'lucide-react';
import { GAMES, getGameColor, getGameName } from '../constants/theme';
import { toast } from 'sonner';

export default function TournamentsPage() {
  const [tournaments, setTournaments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [gameFilter, setGameFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [dialogType, setDialogType] = useState(null);
  const [editData, setEditData] = useState({});
  const [addPlayerSearch, setAddPlayerSearch] = useState('');
  const [addPlayerTarget, setAddPlayerTarget] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const limit = 10;

  useEffect(() => {
    loadTournaments();
  }, [page, gameFilter, statusFilter]);

  const loadTournaments = async () => {
    setLoading(true);
    try {
      const params = { skip: page * limit, limit };
      if (gameFilter) params.game = gameFilter;
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      
      const response = await admin.getTournaments(params);
      setTournaments(response.data.tournaments);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error loading tournaments:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    loadTournaments();
  };

  const openEditDialog = (tournament) => {
    setSelectedTournament(tournament);
    setEditData({
      name: tournament.name,
      description: tournament.description,
      price: tournament.price,
      capacity: tournament.capacity,
      status: tournament.status,
    });
    setDialogType('edit');
  };

  const openDeleteDialog = (tournament) => {
    setSelectedTournament(tournament);
    setDialogType('delete');
  };

  const closeDialog = () => {
    setSelectedTournament(null);
    setDialogType(null);
    setEditData({});
    setAddPlayerSearch('');
  };

  const openAddPlayer = (t) => {
    setSelectedTournament(t);
    setDialogType('addPlayer');
    setAddPlayerSearch('');
  };

  const openManageDialog = async (t) => {
    setSelectedTournament(t);
    setDialogType('manage');
    setLoadingParticipants(true);
    try {
      const res = await tournamentAdmin.getParticipants(t.id);
      setParticipants(res.data);
    } catch (e) {
      setParticipants([]);
      toast.error('Erreur chargement joueurs');
    } finally {
      setLoadingParticipants(false);
    }
  };

  const handleRemovePlayer = async (playerId) => {
    if (!selectedTournament) return;
    try {
      await tournamentAdmin.removePlayer(selectedTournament.id, playerId);
      toast.success('Joueur retiré');
      const res = await tournamentAdmin.getParticipants(selectedTournament.id);
      setParticipants(res.data);
      loadTournaments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleManageAddPlayer = async () => {
    if (!addPlayerSearch.trim() || !selectedTournament) return;
    try {
      const res = await tournamentsApi.addPlayer(selectedTournament.id, addPlayerSearch.trim());
      toast.success(res.data.message || 'Joueur ajouté');
      setAddPlayerSearch('');
      const pRes = await tournamentAdmin.getParticipants(selectedTournament.id);
      setParticipants(pRes.data);
      loadTournaments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleDownloadCsv = async (t) => {
    try {
      const res = await tournamentAdmin.downloadCsv(t.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${t.name}-joueurs.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Erreur export CSV'); }
  };

  const handleDownloadXlsx = async (t) => {
    try {
      const res = await tournamentAdmin.downloadXlsx(t.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${t.name}-joueurs.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Erreur export Excel'); }
  };

  const handleAddPlayer = async () => {
    if (!addPlayerSearch.trim() || !selectedTournament) return;
    try {
      const res = await tournamentsApi.addPlayer(selectedTournament.id, addPlayerSearch.trim());
      toast.success(res.data.message);
      setAddPlayerSearch('');
      loadTournaments();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur');
    }
  };

  const handleUpdate = async () => {
    if (!selectedTournament) return;
    try {
      await admin.updateTournament(selectedTournament.id, editData);
      toast.success('Tournoi mis à jour');
      closeDialog();
      loadTournaments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async () => {
    if (!selectedTournament) return;
    try {
      await admin.deleteTournament(selectedTournament.id);
      toast.success('Tournoi supprimé');
      closeDialog();
      loadTournaments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const totalPages = Math.ceil(total / limit);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      upcoming: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      in_progress: 'bg-green-500/20 text-green-400 border-green-500/30',
      completed: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
    };
    const labels = {
      upcoming: 'À venir',
      in_progress: 'En cours',
      completed: 'Terminé',
      cancelled: 'Annulé',
    };
    return <Badge className={styles[status] || styles.upcoming}>{labels[status] || status}</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Tournois</h1>
          <p className="text-[#666] mt-1">{total} tournois au total</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-[#1a1a1a] border-[#333]">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
              <Input
                placeholder="Rechercher par nom..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-[#252525] border-[#333] text-white placeholder:text-[#666]"
                data-testid="tournaments-search-input"
              />
            </div>
            <Select value={gameFilter || "all"} onValueChange={(v) => { setGameFilter(v === "all" ? "" : v); setPage(0); }}>
              <SelectTrigger className="w-full md:w-48 bg-[#252525] border-[#333] text-white">
                <SelectValue placeholder="Tous les jeux" />
              </SelectTrigger>
              <SelectContent className="bg-[#252525] border-[#333]">
                <SelectItem value="all" className="text-white">Tous les jeux</SelectItem>
                {GAMES.map((game) => (
                  <SelectItem key={game.id} value={game.id} className="text-white">
                    {game.shortName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter || "all"} onValueChange={(v) => { setStatusFilter(v === "all" ? "" : v); setPage(0); }}>
              <SelectTrigger className="w-full md:w-48 bg-[#252525] border-[#333] text-white">
                <SelectValue placeholder="Tous les statuts" />
              </SelectTrigger>
              <SelectContent className="bg-[#252525] border-[#333]">
                <SelectItem value="all" className="text-white">Tous les statuts</SelectItem>
                <SelectItem value="upcoming" className="text-white">À venir</SelectItem>
                <SelectItem value="in_progress" className="text-white">En cours</SelectItem>
                <SelectItem value="completed" className="text-white">Terminé</SelectItem>
                <SelectItem value="cancelled" className="text-white">Annulé</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" className="bg-[#FF6B35] hover:bg-[#E55A25]">
              Rechercher
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Tournaments List */}
      <div className="grid gap-4">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]"></div>
          </div>
        ) : tournaments.length === 0 ? (
          <Card className="bg-[#1a1a1a] border-[#333]">
            <CardContent className="p-8 text-center">
              <Trophy className="w-12 h-12 text-[#666] mx-auto mb-4" />
              <p className="text-[#666]">Aucun tournoi trouvé</p>
            </CardContent>
          </Card>
        ) : (
          tournaments.map((tournament) => (
            <Card key={tournament.id} className="bg-[#1a1a1a] border-[#333] hover:border-[#FF6B35]/50 transition-colors">
              <CardContent className="p-0">
                <div className="flex">
                  {/* Game color strip */}
                  <div 
                    className="w-1 rounded-l-lg"
                    style={{ backgroundColor: getGameColor(tournament.game) }}
                  />
                  
                  <div className="flex-1 p-4">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2 md:gap-4">
                      <div className="flex-1 min-w-0">
                        {/* Header */}
                        <div className="flex items-center gap-2 mb-2">
                          <Badge 
                            className="text-xs"
                            style={{ 
                              backgroundColor: `${getGameColor(tournament.game)}20`,
                              color: getGameColor(tournament.game),
                              borderColor: `${getGameColor(tournament.game)}30`
                            }}
                          >
                            {getGameName(tournament.game)}
                          </Badge>
                          <Badge className="bg-[#252525] text-[#B0B0B0] border-[#333]">
                            {tournament.format}
                          </Badge>
                          {getStatusBadge(tournament.status)}
                          {tournament.is_recurring && (
                            <Badge className="bg-[#4ECDC4]/20 text-[#4ECDC4] border-[#4ECDC4]/30">
                              ↻ Récurrent
                            </Badge>
                          )}
                        </div>
                        
                        {/* Title */}
                        <Link to={`/player/event/tournament/${tournament.id}`} className="block hover:underline">
                          <h3 className="text-lg font-semibold text-white mb-2">
                            {tournament.name}
                          </h3>
                        </Link>
                        
                        {/* Meta */}
                        <div className="flex flex-wrap items-center gap-4 text-sm text-[#666]">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            <span>{formatDate(tournament.date)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            <span>{tournament.venue?.city || 'N/A'}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            <span>{tournament.participants_count || 0} / {tournament.capacity}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Euro className="w-4 h-4" />
                            <span>{tournament.price === 0 ? 'Gratuit' : `${tournament.price}€`}</span>
                          </div>
                        </div>
                        
                        {/* Organizer */}
                        <p className="text-xs text-[#666] mt-2">
                          Organisé par: {tournament.organizer_name}
                        </p>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-1 flex-wrap justify-end shrink-0">
                        <Link to={`/tournament/${tournament.id}/manage`}>
                          <Button variant="ghost" size="icon" className="text-[#666] hover:text-[#FF6B35] hover:bg-[#FF6B35]/10 h-8 w-8" title="Rondes suisses" data-testid={`rounds-${tournament.id}`}>
                            <Swords className="w-4 h-4" />
                          </Button>
                        </Link>
                        <Button variant="ghost" size="icon" onClick={() => openManageDialog(tournament)} className="text-[#666] hover:text-[#4ECDC4] hover:bg-[#4ECDC4]/10 h-8 w-8" title="Gérer les joueurs" data-testid={`manage-players-${tournament.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openAddPlayer(tournament)} className="text-[#666] hover:text-[#4ECDC4] hover:bg-[#4ECDC4]/10 h-8 w-8" title="Ajouter un joueur" data-testid={`add-player-${tournament.id}`}>
                          <UserPlus className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDownloadCsv(tournament)} className="text-[#666] hover:text-blue-400 hover:bg-blue-400/10 h-8 w-8" title="Télécharger CSV" data-testid={`csv-${tournament.id}`}>
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDownloadXlsx(tournament)} className="text-[#666] hover:text-green-400 hover:bg-green-400/10 h-8 w-8" title="Exporter Excel" data-testid={`xlsx-${tournament.id}`}>
                          <FileSpreadsheet className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openEditDialog(tournament)} className="text-[#666] hover:text-[#FF6B35] hover:bg-[#FF6B35]/10 h-8 w-8" title="Modifier" data-testid={`edit-tournament-${tournament.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openDeleteDialog(tournament)} className="text-[#666] hover:text-red-400 hover:bg-red-400/10 h-8 w-8" title="Supprimer">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-[#666] text-sm">
            Page {page + 1} sur {totalPages || 1}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="border-[#333] text-white hover:bg-[#333]"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setPage(p => p + 1)}
              disabled={page >= totalPages - 1}
              className="border-[#333] text-white hover:bg-[#333]"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={dialogType === 'edit'} onOpenChange={closeDialog}>
        <DialogContent className="bg-[#1a1a1a] border-[#333] text-white">
          <DialogHeader>
            <DialogTitle>Modifier le tournoi</DialogTitle>
            <DialogDescription className="text-[#666]">
              Modifier les informations du tournoi
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-[#666]">Nom</label>
              <Input
                value={editData.name || ''}
                onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                className="bg-[#252525] border-[#333] text-white"
              />
            </div>
            <div>
              <label className="text-sm text-[#666]">Description</label>
              <Input
                value={editData.description || ''}
                onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                className="bg-[#252525] border-[#333] text-white"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-[#666]">Prix (€)</label>
                <Input
                  type="number"
                  value={editData.price || 0}
                  onChange={(e) => setEditData({ ...editData, price: parseFloat(e.target.value) })}
                  className="bg-[#252525] border-[#333] text-white"
                />
              </div>
              <div>
                <label className="text-sm text-[#666]">Capacité</label>
                <Input
                  type="number"
                  value={editData.capacity || 0}
                  onChange={(e) => setEditData({ ...editData, capacity: parseInt(e.target.value) })}
                  className="bg-[#252525] border-[#333] text-white"
                />
              </div>
            </div>
            <div>
              <label className="text-sm text-[#666]">Statut</label>
              <Select value={editData.status || ''} onValueChange={(v) => setEditData({ ...editData, status: v })}>
                <SelectTrigger className="bg-[#252525] border-[#333] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#252525] border-[#333]">
                  <SelectItem value="upcoming" className="text-white">À venir</SelectItem>
                  <SelectItem value="in_progress" className="text-white">En cours</SelectItem>
                  <SelectItem value="completed" className="text-white">Terminé</SelectItem>
                  <SelectItem value="cancelled" className="text-white">Annulé</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} className="border-[#333] text-white">
              Annuler
            </Button>
            <Button onClick={handleUpdate} className="bg-[#FF6B35] hover:bg-[#E55A25]">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={dialogType === 'delete'} onOpenChange={closeDialog}>
        <DialogContent className="bg-[#1a1a1a] border-[#333] text-white">
          <DialogHeader>
            <DialogTitle>Supprimer le tournoi</DialogTitle>
            <DialogDescription className="text-[#666]">
              Êtes-vous sûr de vouloir supprimer "{selectedTournament?.name}" ? Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} className="border-[#333] text-white">
              Annuler
            </Button>
            <Button onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Players Dialog */}
      <Dialog open={dialogType === 'manage'} onOpenChange={closeDialog}>
        <DialogContent className="bg-[#1a1a1a] border-[#333] text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-[#4ECDC4]" />Gestion des joueurs - {selectedTournament?.name}</DialogTitle>
            <DialogDescription className="text-[#666]">{participants.length} / {selectedTournament?.capacity} joueurs inscrits</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input placeholder="Email ou pseudo du joueur à ajouter..." value={addPlayerSearch} onChange={(e) => setAddPlayerSearch(e.target.value)}
                className="bg-[#252525] border-[#333] text-white" data-testid="manage-add-player-input"
                onKeyDown={(e) => e.key === 'Enter' && handleManageAddPlayer()} />
              <Button onClick={handleManageAddPlayer} className="bg-[#FF6B35] hover:bg-[#E55A25] gap-2" data-testid="manage-add-player-btn"><UserPlus className="w-4 h-4" />Ajouter</Button>
            </div>
            <div className="max-h-80 overflow-y-auto space-y-2">
              {loadingParticipants ? (
                <div className="flex justify-center py-6"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#FF6B35]" /></div>
              ) : participants.length === 0 ? (
                <p className="text-[#666] text-center py-4">Aucun joueur inscrit</p>
              ) : participants.map(p => (
                <div key={p.id} className="flex items-center justify-between p-3 rounded-lg bg-[#252525]" data-testid={`participant-${p.id}`}>
                  <div>
                    <p className="text-white font-medium">{p.name || p.pseudo || 'Joueur'}</p>
                    <p className="text-[#666] text-sm">{p.email}</p>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => handleRemovePlayer(p.id)}
                    className="text-[#666] hover:text-red-400 hover:bg-red-400/10" title="Retirer le joueur"
                    data-testid={`remove-player-${p.id}`}>
                    <UserMinus className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Player Dialog */}
      <Dialog open={dialogType === 'addPlayer'} onOpenChange={closeDialog}>
        <DialogContent className="bg-[#1a1a1a] border-[#333] text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-[#4ECDC4]" />Ajouter un joueur</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#666]">au tournoi "{selectedTournament?.name}"</p>
          <div className="space-y-3">
            <Input value={addPlayerSearch} onChange={(e) => setAddPlayerSearch(e.target.value)}
              placeholder="Email, nom ou pseudo du joueur" className="bg-[#252525] border-[#333] text-white"
              data-testid="add-player-search" onKeyDown={(e) => e.key === 'Enter' && handleAddPlayer()} />
            <p className="text-xs text-[#666]">Si le joueur n'a pas de compte, il sera inscrit comme invité.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} className="border-[#333] text-white">Annuler</Button>
            <Button onClick={handleAddPlayer} className="bg-[#4ECDC4] hover:bg-[#4ECDC4]/80 text-black" data-testid="add-player-confirm">Ajouter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
